import React from 'react'

// Components
import { IconButton, Tooltip } from '@mui/material'
import { Close, InsertDriveFile, Visibility } from '@mui/icons-material'
import { AttachmentPreviewModal } from './AttachmentPreviewModal'

// Logic
import { useAttachmentPreviewLogic } from './AttachmentPreview.logic'

// Types
import { AttachmentPreviewProps } from './types'

// Styles
import styles from './AttachmentPreview.module.scss'

export const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachments,
  onRemoveAttachment,
  onAttach,
  maxAttachments = 4,
}) => {
  const {
    previewModalOpen,
    selectedAttachment,
    selectedIndex,
    handlePreviewClick,
    handleClosePreview,
    getFileIcon,
    isImage,
  } = useAttachmentPreviewLogic()

  if (attachments.length === 0) {
    return (
      <div className={styles.attachmentEmptyState}>
        <div className={styles.attachmentPlaceholder} onClick={onAttach}>
          <InsertDriveFile className={styles.placeholderIcon} />
          <span className={styles.placeholderText}>
            Attach Documents (PDF, Image)
          </span>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={styles.attachmentPreviewContainer}>
        <div className={styles.attachmentGrid}>
          {attachments.map((attachment, index) => (
            <div key={index} className={styles.attachmentItem}>
              <div className={styles.attachmentHeader}>
                <Tooltip title="Remove attachment">
                  <IconButton
                    className={styles.removeButton}
                    onClick={() => onRemoveAttachment(index)}
                    size="small"
                  >
                    <Close className={styles.removeIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Preview attachment">
                  <IconButton
                    className={styles.previewButton}
                    onClick={() => handlePreviewClick(attachment, index)}
                    size="small"
                  >
                    <Visibility className={styles.previewIcon} />
                  </IconButton>
                </Tooltip>
              </div>

              <div
                className={styles.attachmentContent}
                onClick={() => handlePreviewClick(attachment, index)}
              >
                {isImage(attachment.mimetype, attachment.filename) ? (
                  <div className={styles.thumbnailContainer}>
                    <img
                      src={attachment.thumbnail}
                      alt={attachment.filename}
                      className={styles.attachmentThumbnail}
                    />
                  </div>
                ) : (
                  <div className={styles.fileIconContainer}>
                    {getFileIcon(attachment.filename, attachment.mimetype)}
                  </div>
                )}
              </div>

              <div className={styles.attachmentInfo}>
                <Tooltip title={attachment.filename}>
                  <div className={styles.filename}>{attachment.filename}</div>
                </Tooltip>
                <div className={styles.fileSize}>
                  {(attachment.size / 1024).toFixed(1)} KB
                </div>
              </div>
            </div>
          ))}

          {attachments.length < maxAttachments && (
            <div className={`${styles.attachmentItem} ${styles.addMore}`}>
              <div className={styles.addMoreContent} onClick={onAttach}>
                <InsertDriveFile className={styles.addMoreIcon} />
                <span className={styles.addMoreText}>Add More</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <AttachmentPreviewModal
        open={previewModalOpen}
        attachment={selectedAttachment}
        attachmentIndex={selectedIndex}
        onClose={handleClosePreview}
        onRemove={(index: number) => {
          onRemoveAttachment(index)
          handleClosePreview()
        }}
      />
    </>
  )
}
